import React, {FormEvent, useEffect, useMemo, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {AccountSettingsId} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {Trans} from '@common/i18n/trans';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';
import {reloadAccountUsage} from '@app/editor/use-account-usage';

interface AiModel {
  id: string;
  name: string;
  supports_images?: boolean;
}

interface AiSettingsPayload {
  mode: 'platform' | 'user';
  provider: 'openai' | 'gemini' | 'openrouter';
  api_key_set: boolean;
  api_key_hint?: string | null;
  base_url?: string | null;
  site_url?: string | null;
  text_model?: string | null;
  project_model?: string | null;
  image_model?: string | null;
  active?: {
    uses_user_key: boolean;
    billing_source: string;
  };
  platform?: {
    provider: string;
    text_model?: string | null;
    project_model?: string | null;
    image_model?: string | null;
  };
}

interface AiSettingsResponse {
  settings: AiSettingsPayload;
  models: {
    provider: string;
    models: AiModel[];
    fallback: AiModel[];
  };
}

const providers = [
  {id: 'openrouter', label: 'OpenRouter'},
  {id: 'openai', label: 'OpenAI'},
  {id: 'gemini', label: 'Google Gemini'},
] as const;

export function AiProviderSettingsPanel() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    mode: 'platform',
    provider: 'openrouter',
    api_key: '',
    clear_api_key: false,
    base_url: '',
    site_url: '',
    text_model: '',
    project_model: '',
    image_model: '',
  });
  const {data, isLoading} = useQuery({
    queryKey: ['account-ai-settings'],
    queryFn: () => apiClient.get<AiSettingsResponse>('account/ai-settings').then(r => r.data),
  });
  const {data: modelData} = useQuery({
    queryKey: ['account-ai-settings-models', form.provider],
    queryFn: () =>
      apiClient
        .get<AiSettingsResponse['models']>('account/ai-settings/models', {
          params: {provider: form.provider},
        })
        .then(r => r.data),
    enabled: !!form.provider,
  });
  const save = useMutation({
    mutationFn: (payload: Record<string, any>) =>
      apiClient.put<AiSettingsResponse>('account/ai-settings', payload).then(r => r.data),
    onSuccess: response => {
      queryClient.setQueryData(['account-ai-settings'], response);
      reloadAccountUsage();
      toast(message('AI provider settings updated'));
    },
  });

  useEffect(() => {
    if (!data?.settings) return;
    setForm(prev => ({
      ...prev,
      mode: data.settings.mode || 'platform',
      provider: data.settings.provider || 'openrouter',
      api_key: '',
      clear_api_key: false,
      base_url: data.settings.base_url || '',
      site_url: data.settings.site_url || '',
      text_model: data.settings.text_model || '',
      project_model: data.settings.project_model || '',
      image_model: data.settings.image_model || '',
    }));
  }, [data?.settings]);

  const models = useMemo(() => modelData?.models ?? data?.models.models ?? [], [data, modelData]);
  const textModels = models.filter(model => !model.supports_images);
  const imageModels = models.filter(model => model.supports_images);

  const update = (key: string, value: string | boolean) => {
    setForm(prev => ({...prev, [key]: value}));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(form);
  };

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.AiProvider}
      title={<Trans message="AI provider and models" />}
      description={<Trans message="Use the platform AI setup or bring your own provider API key. Requests made with your own key do not consume platform AI quota." />}
    >
      <form className="space-y-18" onSubmit={handleSubmit}>
        {isLoading ? (
          <div className="text-sm text-muted">Loading AI settings...</div>
        ) : (
          <>
            <div className="rounded-lg border border-divider p-12">
              <div className="mb-8 text-sm font-semibold">Billing mode</div>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <ChoiceButton
                  active={form.mode === 'platform'}
                  title="Use platform AI"
                  body={`Uses ${data?.settings.platform?.provider ?? 'configured provider'} and counts against your plan limits.`}
                  onClick={() => update('mode', 'platform')}
                />
                <ChoiceButton
                  active={form.mode === 'user'}
                  title="Use my API key"
                  body="Runs against your provider account, so the platform does not pay for or quota those requests."
                  onClick={() => update('mode', 'user')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-14 md:grid-cols-2">
              <Field label="Provider">
                <select
                  className="w-full rounded-md border border-divider bg-alt px-10 py-8 text-sm"
                  value={form.provider}
                  onChange={e => update('provider', e.target.value)}
                >
                  {providers.map(provider => (
                    <option key={provider.id} value={provider.id}>{provider.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="API key">
                <input
                  className="w-full rounded-md border border-divider bg-alt px-10 py-8 text-sm"
                  type="password"
                  value={form.api_key}
                  onChange={e => update('api_key', e.target.value)}
                  placeholder={data?.settings.api_key_set ? `Saved: ${data.settings.api_key_hint}` : 'Paste provider API key'}
                  disabled={form.mode !== 'user'}
                />
              </Field>
            </div>

            {data?.settings.api_key_set && form.mode === 'user' && (
              <label className="flex items-center gap-8 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={form.clear_api_key}
                  onChange={e => update('clear_api_key', e.target.checked)}
                />
                Remove saved API key on save
              </label>
            )}

            <div className="grid grid-cols-1 gap-14 md:grid-cols-2">
              <Field label="Base URL">
                <input
                  className="w-full rounded-md border border-divider bg-alt px-10 py-8 text-sm"
                  value={form.base_url}
                  onChange={e => update('base_url', e.target.value)}
                  placeholder={form.provider === 'openrouter' ? 'https://openrouter.ai/api/v1' : 'Optional custom endpoint'}
                  disabled={form.mode !== 'user'}
                />
              </Field>
              <Field label="Site URL">
                <input
                  className="w-full rounded-md border border-divider bg-alt px-10 py-8 text-sm"
                  value={form.site_url}
                  onChange={e => update('site_url', e.target.value)}
                  placeholder="Optional, used by OpenRouter"
                  disabled={form.mode !== 'user'}
                />
              </Field>
            </div>

            <div className="rounded-lg border border-divider p-12">
              <div className="mb-8 text-sm font-semibold">Available models</div>
              <div className="mb-12 max-h-160 overflow-auto rounded-md border border-divider bg-alt p-8 text-xs text-muted">
                {models.length ? models.slice(0, 80).map(model => (
                  <div key={model.id} className="flex items-center justify-between gap-10 border-b border-divider py-4 last:border-b-0">
                    <span>{model.name || model.id}</span>
                    <code className="text-[11px]">{model.id}</code>
                  </div>
                )) : 'No models loaded.'}
              </div>

              <div className="grid grid-cols-1 gap-14 md:grid-cols-3">
                <ModelField label="Text model" value={form.text_model} models={textModels} onChange={value => update('text_model', value)} />
                <ModelField label="Project builder model" value={form.project_model} models={textModels} onChange={value => update('project_model', value)} />
                <ModelField label="Image model" value={form.image_model} models={imageModels.length ? imageModels : models} onChange={value => update('image_model', value)} />
              </div>
            </div>

            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-md bg-primary px-14 py-9 text-sm font-semibold text-white disabled:opacity-60"
              disabled={save.isPending}
            >
              {save.isPending ? 'Saving...' : 'Save AI settings'}
            </button>
          </>
        )}
      </form>
    </AccountSettingsPanel>
  );
}

function ChoiceButton({active, title, body, onClick}: {active: boolean; title: string; body: string; onClick: () => void}) {
  return (
    <button
      type="button"
      className={`rounded-lg border p-12 text-left ${active ? 'border-primary bg-primary/10' : 'border-divider bg-alt'}`}
      onClick={onClick}
    >
      <div className="mb-4 text-sm font-semibold">{title}</div>
      <div className="text-xs text-muted leading-relaxed">{body}</div>
    </button>
  );
}

function Field({label, children}: {label: string; children: React.ReactNode}) {
  return (
    <label className="block">
      <div className="mb-6 text-xs font-semibold text-muted">{label}</div>
      {children}
    </label>
  );
}

function ModelField({label, value, models, onChange}: {label: string; value: string; models: AiModel[]; onChange: (value: string) => void}) {
  return (
    <Field label={label}>
      <input
        className="mb-6 w-full rounded-md border border-divider bg-alt px-10 py-8 text-sm"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Provider model ID"
        list={`${label.replace(/\s+/g, '-')}-models`}
      />
      <datalist id={`${label.replace(/\s+/g, '-')}-models`}>
        {models.map(model => (
          <option key={model.id} value={model.id}>{model.name}</option>
        ))}
      </datalist>
    </Field>
  );
}
